/* AdvancedPromptDetails — Collapsible "Add more detail" panel for AI generation
 * forms (Generator page + AI Post Manager). Lets the user paste:
 *   - A reference post the AI should match in structure/voice (style transfer)
 *   - Extra rules: banned words, required CTAs, brand quirks, etc.
 *
 * Both fields are optional. When empty, the backend ignores them and behavior
 * is unchanged. When present, the LLM is told to mirror the reference's
 * structure (not copy text) and obey the extra rules strictly.
 *
 * Props:
 *   styleExample: string                 // paste-here reference post
 *   onStyleExampleChange(s: string): void
 *   extraInstructions: string            // free-form rules
 *   onExtraInstructionsChange(s: string): void
 *   defaultOpen?: boolean                // expand on mount (e.g. when values are pre-filled)
 *   className?: string
 */
import { useState, useEffect } from 'react';
import { ChevronDown, Sparkles, X } from 'lucide-react';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';

const STYLE_MAX = 4000;
const EXTRA_MAX = 1000;

export const AdvancedPromptDetails = ({
  styleExample = '',
  onStyleExampleChange,
  extraInstructions = '',
  onExtraInstructionsChange,
  defaultOpen = false,
  className = '',
}) => {
  const [open, setOpen] = useState(defaultOpen);

  // Auto-expand once if the parent populates a value (e.g. from a saved template).
  useEffect(() => {
    if ((styleExample || extraInstructions) && !open) setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filledCount = [styleExample, extraInstructions].filter((s) => s && s.trim()).length;

  return (
    <div
      className={`rounded-lg border ${open ? 'border-indigo-200 bg-indigo-50/40' : 'border-zinc-200 bg-white'} ${className}`}
      data-testid="advanced-prompt-details"
    >
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left"
        data-testid="advanced-prompt-toggle"
      >
        <span className="flex items-center gap-2 text-sm font-medium text-zinc-800">
          <Sparkles className="w-4 h-4 text-indigo-600" />
          Add more detail
          {filledCount > 0 && (
            <span className="ml-1 inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-indigo-600 text-white">
              {filledCount}
            </span>
          )}
        </span>
        <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-3">
          {/* Style reference post */}
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-zinc-700">
                Reference post (style template)
              </label>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-zinc-500">
                  {styleExample.length}/{STYLE_MAX}
                </span>
                {styleExample && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-5 px-1 text-zinc-500 hover:text-red-600"
                    onClick={() => onStyleExampleChange('')}
                    data-testid="clear-style-example"
                  >
                    <X className="w-3 h-3" />
                  </Button>
                )}
              </div>
            </div>
            <Textarea
              value={styleExample}
              onChange={(e) => {
                const v = e.target.value;
                onStyleExampleChange(v.length > STYLE_MAX ? v.slice(0, STYLE_MAX) : v);
              }}
              placeholder={`Paste a post you love. The AI will match its structure, hooks, paragraph rhythm, list formatting, and CTA style — but write fresh content for YOUR topic.\n\nExample:\nOne of the biggest mistakes people make on LinkedIn...\n\n1. Pick ONE core topic\n   • leadership\n   • AI\n   • marketing\n\n2. Break it into micro-topics\n\nHow far ahead do you usually plan your content?`}
              rows={6}
              className="text-xs"
              data-testid="style-example-textarea"
            />
            <p className="text-[10px] text-zinc-500 leading-snug">
              Paste a winning LinkedIn / Instagram / blog post. The AI mirrors the format (lists, line breaks, opening hook, closing question), not the words.
            </p>
          </div>

          {/* Extra free-form rules */}
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <label className="text-xs font-medium text-zinc-700">
                Extra instructions
              </label>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-zinc-500">
                  {extraInstructions.length}/{EXTRA_MAX}
                </span>
                {extraInstructions && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-5 px-1 text-zinc-500 hover:text-red-600"
                    onClick={() => onExtraInstructionsChange('')}
                    data-testid="clear-extra-instructions"
                  >
                    <X className="w-3 h-3" />
                  </Button>
                )}
              </div>
            </div>
            <Textarea
              value={extraInstructions}
              onChange={(e) => {
                const v = e.target.value;
                onExtraInstructionsChange(v.length > EXTRA_MAX ? v.slice(0, EXTRA_MAX) : v);
              }}
              placeholder={`Required CTAs, banned words, brand quirks, audience-specific notes…\n\nExamples:\n• Always end with "Book a free strategy call at posttosocial.com"\n• Never mention specific dollar amounts\n• Speak to founders (not enterprises)\n• Use US spelling, no emojis at the start of paragraphs`}
              rows={3}
              className="text-xs"
              data-testid="extra-instructions-textarea"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedPromptDetails;
